"use client";

import { UserCheck, Store, Check, type LucideIcon } from "lucide-react";

import { Container } from "@/components/primitives/Container";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Reveal } from "@/components/motion/Reveal";
import { getPartners } from "@/content/partners";
import { useLocale } from "@/lib/i18n/provider";
import type { PartnerTier } from "@/content/partners";

const tierIcons: Record<PartnerTier["icon"], LucideIcon> = {
  UserCheck,
  Store,
};

const tierAccents: Record<PartnerTier["type"], string> = {
  professional:
    "radial-gradient(50% 50% at 50% 50%, var(--tier-glow-technology) 0%, transparent 70%)",
  business:
    "radial-gradient(50% 50% at 50% 50%, var(--tier-glow-retail) 0%, transparent 70%)",
};

export function PartnerTiers() {
  const { locale } = useLocale();
  const copy = getPartners(locale);

  return (
    <section id="tiers" className="relative py-24 md:py-32">
      <Container>
        <div className="mx-auto max-w-[60ch] text-center">
          <Reveal delay={0}>
            <Eyebrow>{copy.tiers.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.07}>
            <h2 className="mt-4 text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.06] tracking-[-0.025em] text-[var(--color-text-primary)]">
              {copy.tiers.title}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-5 text-[clamp(1rem,1.2vw,1.125rem)] leading-relaxed text-[var(--color-text-secondary)]">
              {copy.tiers.body}
            </p>
          </Reveal>
        </div>

        <ul className="mt-16 grid gap-6 lg:grid-cols-2">
          {copy.tiers.items.map((tier, i) => {
            const Icon = tierIcons[tier.icon];
            return (
              <li key={tier.type} className="flex">
                <Reveal delay={0.07 * i} className="flex w-full">
                  <article className="group relative flex w-full flex-col overflow-hidden rounded-3xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] p-8 transition-all duration-300 hover:border-[var(--color-surface-border-strong)] hover:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.12)]">
                    <div
                      aria-hidden
                      className="pointer-events-none absolute -right-12 -top-12 h-64 w-64 rounded-full opacity-20 blur-3xl transition-opacity duration-500 group-hover:opacity-40"
                      style={{ background: tierAccents[tier.type] }}
                    />

                    <span className="relative mb-6 inline-flex w-fit items-center rounded-full border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                      {tier.eyebrow}
                    </span>

                    <div className="relative mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-accent-soft)] text-[var(--color-accent-base)]">
                      <Icon size={22} strokeWidth={1.75} />
                    </div>

                    <h3 className="relative text-xl font-semibold tracking-tight text-[var(--color-text-primary)]">
                      {tier.title}
                    </h3>

                    <p className="relative mt-3 flex-1 text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
                      {tier.body}
                    </p>

                    {tier.offer ? (
                      <p className="relative mt-5 inline-flex w-fit items-center rounded-full border border-[var(--color-accent-base)]/35 bg-[var(--color-accent-soft)] px-3.5 py-1.5 text-[12px] font-semibold leading-snug text-[var(--color-accent-base)]">
                        {tier.offer}
                      </p>
                    ) : null}

                    <div className="relative mt-6 h-px w-full bg-[var(--color-surface-border)]" />

                    <ul className="relative mt-5 flex flex-col gap-3">
                      {tier.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-2.5 text-sm text-[var(--color-text-secondary)]">
                          <Check
                            size={14}
                            strokeWidth={2.5}
                            className="mt-0.5 shrink-0 text-[var(--color-accent-base)]"
                            aria-hidden="true"
                          />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              </li>
            );
          })}
        </ul>
      </Container>
    </section>
  );
}

export default PartnerTiers;
